import { parseCookies, setCookie } from 'nookies'
import { ReactNode, createContext, useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { api } from '../../services/api'
import { FlowNextStep, resolveJourneyId, resolveNextFlowStep } from '../../services/argusFlowNavigation'
import { discountFromFlowStep } from '../../utils/flowDiscount'
import { defaultThankYouUrl } from '../../utils/flowThankYou'
import { handleHTTPErrorMessage } from '../../utils/handleHTTPErrorMessage'
import { queryString } from '../../utils/queryString'
import { convertParamsToObject, getParameterByName, getPathFromUrl } from '../../utils/urlParams'
import { DecisionsProps, DiscountProps, FlowProps, FlowStepProps } from './types/Flow'

export interface FlowContextProps {
	isLoading: boolean;
	setIsLoading: (value: boolean) => void;
	findOne: (hash: string) => Promise<FlowProps>;
	findStep: (offerCode: string, stepOfferCode: string) => Promise<FlowStepProps>;

	decline: () => Promise<void>;
	getFlowUUID: () => string;
	getStepOfferCode: () => string;
	getMainOfferCode: () => Promise<string>;

	isUpsellDiscountModalOpen: boolean
	setIsUpsellDiscountModalOpen: (isOpen: boolean) => void
	handleRejectFlow: () => void
	rejectV2: () => Promise<boolean>

	purchases: string[]
	setPurchases: (value: string[]) => void
	flow: FlowProps,
	step: FlowStepProps,
	accept: DecisionsProps,
	reject: DecisionsProps,
	setUpsellDiscount: (discount: DiscountProps | null) => void
	upsellDiscount: DiscountProps | null,
	isDiscountAccepted: boolean,
	setIsDiscountAccepted: (value: boolean) => void
}

export interface FlowProviderProps {
	children: ReactNode;
}

const FlowContext = createContext({} as FlowContextProps);

export const FlowProvider = ({ children }: FlowProviderProps) => {
	const [isLoading, setIsLoading] = useState(true)
	const [purchases, setPurchases] = useState<string[]>([])
	const [flow, setFlow] = useState<FlowProps>(null)
	const [step, setStep] = useState<FlowStepProps>(null)
	const [accept, setAccept] = useState<DecisionsProps>(null);
	const [reject, setReject] = useState<DecisionsProps>(null);
	const [isDiscountAccepted, setIsDiscountAccepted] = useState(false);
	const [isUpsellDiscountModalOpen, setIsUpsellDiscountModalOpen] = useState(false);
	const [upsellDiscount, setUpsellDiscount] = useState<DiscountProps | null>(null);

	const findOne = async (pk: string): Promise<FlowProps> => {
		setIsLoading(true);
		return await new Promise((resolve, reject) => {
			api.get<FlowProps>(`flow/${String(pk)}`)
				.then(async response => {
					if (!response.data?.pk) {
						resolve(null);
						return;
					}
					setFlow(response.data);
					resolve(response.data);
				})
				.catch(error => {
					toast.error(handleHTTPErrorMessage(error));
					reject(error);
				})
				.finally(() => setTimeout(() => setIsLoading(false), 1000));
		});
	};

	const findStep = async (offerCode: string, stepOfferCode: string): Promise<FlowStepProps> => {
		if (!offerCode || !stepOfferCode) return;

		let params = {
			fromMidas: true,
			upsell_code: stepOfferCode,
			rand: Math.random(),
			purchases: getParameterByName('purchases') ?? JSON.stringify(purchases),
		};

		setIsLoading(true);
		return await new Promise((resolve, reject) => {
			api.get<FlowStepProps>(`flow-step/${offerCode}${queryString(params)}`)
				.then(async response => {
					setStep(response.data);

					setAccept(response.data?.decisions?.accept);
					setReject(response.data?.decisions?.reject);

					resolve(response.data);
				})
				.catch(error => {
					toast.error(handleHTTPErrorMessage(error));
					reject(error);
				})
				.finally(() => setTimeout(() => setIsLoading(false), 1000));
		});
	};

	const getHash = () => {
		const fromURL = getParameterByName('hash');
		const hash = fromURL;

		return hash;
	};

	/**
	 * ==================== FUNIL V2 (Argus) ====================
	 *
	 * O Midas era 100% v1 (Dynamo): a recusa do upsell resolvia por
	 * step.decisions.reject. Funil v2 não tem esse shape — a recusa é um salto
	 * no grafo via /flow/next com decision=reject, e era o elo que faltava na
	 * cadeia: o rail respondia certo desde o início e nenhum cliente chamava.
	 *
	 * Detecção espelha o Bolt: v1 primeiro (nada muda para quem tem funil v1),
	 * e só na ausência dele tenta o v2. A chave do Argus vem da própria oferta
	 * (OfferResource.argus_key do Payments), igual ao Bolt.
	 */
	const offerArgusKey = async (offerCode: string): Promise<string | null> => {
		try {
			const response = await api.get(`offer/${offerCode}`);
			return (response.data as { argus_key?: string })?.argus_key ?? null;
		} catch {
			return null;
		}
	};

	const navigateV2 = (next: FlowNextStep) => {
		// Página de obrigado do funil: destino final, sem contexto de ETAPA a
		// carregar — mas os params atuais (order_hash da compra, identidade da
		// jornada) seguem viajando: é da URL do obrigado que o tracking amarra
		// a venda à jornada.
		if (next.is_thankyou && next.url) {
			const currentParams = convertParamsToObject(window.location.search);
			window.parent.location.href = `${getPathFromUrl(next.url)}${queryString(currentParams)}`;
			return;
		}

		if (!next.url) {
			window.parent.location.href = defaultThankYouUrl(
				process.env.REACT_APP_CHECKOUT_URL,
				getHash(),
				window.location.search
			);
			return;
		}

		const urlParams = convertParamsToObject(window.location.search);
		const params: Record<string, string> = {
			...urlParams,
			currentOfferCode: next.offer_code ?? '',
			purchases: getParameterByName('purchases') ?? JSON.stringify(purchases),
		};

		if (next.offer_code) {
			setCookie(undefined, `ticto@currentStep`, JSON.stringify(next.offer_code), {
				maxAge: 60 * 60 * 24, // 24 horas
			});
		}

		window.parent.location.href = `${getPathFromUrl(next.url)}${queryString(params)}`;
	};

	/**
	 * Recusa v2. Devolve true quando assumiu a navegação; false manda o
	 * chamador seguir o caminho v1/fallback existente — a recusa NUNCA pode
	 * prender o comprador, então qualquer falha aqui vira fallback.
	 */
	const rejectV2 = async (): Promise<boolean> => {
		const stepOfferCode = getStepOfferCode();
		if (!stepOfferCode) return false;

		const apiKey = await offerArgusKey(stepOfferCode);
		if (!apiKey) return false;

		// Salto a partir do node de desconto quando o modal já foi mostrado:
		// o desconto costuma linkar a MESMA oferta do upsell, e sem o node_id
		// o segundo "não" resolveria de volta para o upsell (loop).
		const next = await resolveNextFlowStep({
			apiKey,
			offerCode: upsellDiscount?.offer_code ?? stepOfferCode,
			decision: 'reject',
			orderHash: getHash(),
			nodeId: upsellDiscount?.node_id ?? null,
			journeyId: resolveJourneyId() ?? null,
		});

		if (!next) return false;

		// Node de desconto no caminho: mostra o modal de recuperação (o mesmo
		// do v1). Aceitar compra a oferta linkada ao node com o cupom oculto
		// que o Phoenix criou na publicação. Recusar cai de novo aqui,
		// agora com o node_id do desconto, e segue adiante.
		const discount = discountFromFlowStep(next);

		if (discount && !isDiscountAccepted && !upsellDiscount) {
			setUpsellDiscount(discount);
			setIsUpsellDiscountModalOpen(true);
			return true;
		}

		navigateV2(next);
		return true;
	};

	const handleRejectFlow = async () => {
		const reject = document.querySelectorAll('.ticto-refuse-button');
		let olderButtonText: string[] = [];

		for (let i = 0; i < reject.length; i++) {
			olderButtonText.push(reject[i].innerHTML);
			reject[i].innerHTML = 'Por favor, aguarde...';
		}

		// Sem funil v1 na sessão, a recusa é do funil v2 (Argus). A ordem
		// espelha o Bolt: v1 primeiro, v2 na ausência, fallback no fim.
		if (!getFlowUUID()) {
			const handled = await rejectV2();
			if (handled) {
				for (let i = 0; i < reject.length; i++) {
					reject[i].innerHTML = olderButtonText[i];
				}
				return;
			}
		}

		const mainOfferCode = await getMainOfferCode();
		const stepOfferCode = getStepOfferCode();
		const step = await findStep(mainOfferCode, stepOfferCode);
		const stepReject = step?.decisions?.reject;
		const rejectDiscount = stepReject?.discount;

		if (rejectDiscount && !isDiscountAccepted) {
			setUpsellDiscount(rejectDiscount);
			setIsUpsellDiscountModalOpen(true);
		} else {
			decline();
		}
	}

	const decline = async () => {
		const reject = document.querySelectorAll('.ticto-refuse-button');
		let olderButtonText: string[] = [];
		for (let i = 0; i < reject.length; i++) {
			olderButtonText.push(reject[i].innerHTML);
			reject[i].innerHTML = 'Por favor, aguarde...';
		}

		// Segundo "não" do funil v2 (o comprador recusou o modal de desconto):
		// segue o grafo a partir do node de desconto. Falhou → fallback padrão.
		if (!getFlowUUID() && upsellDiscount?.node_id) {
			const handled = await rejectV2();
			if (handled) {
				for (let i = 0; i < reject.length; i++) {
					reject[i].innerHTML = olderButtonText[i];
				}
				return;
			}
		}

		let params: any = String(window.location.search).replace('?', '');
		let downsellURL: string = `${
			process.env.REACT_APP_CHECKOUT_URL
		}/thanks/${getHash()}?withoutFrame=true&${params}`;

		const mainOfferCode = await getMainOfferCode();
		const stepOfferCode = getStepOfferCode();
		const step = await findStep(mainOfferCode, stepOfferCode);
		const stepReject = step?.decisions?.reject;

		const purchases = getParameterByName('purchases')

		if (stepReject && stepReject.url) {
			let stepURL = getPathFromUrl(stepReject.url);
			let stepParams: any = String(stepReject.url).split('?')[1];
			stepParams = stepParams ? convertParamsToObject(stepParams) : {};

			let urlParams = convertParamsToObject(window.location.search);

			params = { ...urlParams, ...stepParams };

			params.currentOfferCode = step.decisions.reject.offer_code;
			params.purchases = purchases?.length > 0 ? purchases : getParameterByName('purchases');

			setCookie(undefined, `ticto@currentStep`,
				JSON.stringify(step.decisions.reject.offer_code),
				{
					maxAge: 60 * 60 * 24, // 24 hours
				}
			)

			downsellURL = `${stepURL}${queryString(params)}`;
		}

		// Retornando o texto do botão para o padrão anterior.
		for (let i = 0; i < reject.length; i++) {
			reject[i].innerHTML = olderButtonText[i];
		}

		setTimeout(() => {
			downsellURL.includes('withoutFrame')
				? (window.parent.location.href = downsellURL)
				: (window.location.href = downsellURL);

			setIsLoading(true);
		}, 500);
		return;
	};

	const getFlowUUID = () => {
		const byURL = getParameterByName('currentFlow');
		const byURL2 = getParameterByName('flow');

		let cookies = parseCookies();
		let flowCookie = cookies[`ticto@currentFlow`] && JSON.parse(cookies[`ticto@currentFlow`]);
		const byCookie = flowCookie?.pk;

		let byLocalStorage = localStorage.getItem('ticto@currentFlow');
		byLocalStorage = byLocalStorage && JSON.parse(byLocalStorage);

		return byURL ?? byURL2 ?? byCookie ?? byLocalStorage;
	};

	const getMainOfferCode = async (): Promise<string> => {
		return await new Promise(async (resolve, reject) => {
			const byURL = getParameterByName('currentFlowMainOffer');

			let cookies = parseCookies();
			let byCookie = cookies[`ticto@currentFlow`] && JSON.parse(cookies[`ticto@currentFlow`]);
			byCookie = byCookie?.offer_code;

			let byLocalStorage = localStorage.getItem('ticto@mainOfferCode');
			byLocalStorage = byLocalStorage && JSON.parse(byLocalStorage);

			if (!byURL && !byCookie && !byLocalStorage) {
				const flow = await findOne(getFlowUUID());
				resolve(flow.product.offers[0].code);
			}

			return resolve(byURL ?? byCookie ?? byLocalStorage);
		});
	};

	const getStepOfferCode = () => {
		const byURL = getParameterByName('currentOfferCode');

		let cookies = parseCookies();
		let byCookie = cookies[`ticto@currentStep`] && JSON.parse(cookies[`ticto@currentStep`]);

		let byLocalStorage = localStorage.getItem('ticto@currentOfferCode');
		byLocalStorage = byLocalStorage && JSON.parse(byLocalStorage);

		return byURL ?? byCookie ?? byLocalStorage;
	};

	const value = {
		isLoading,
		setIsLoading,

		findOne,
		findStep,
		decline,

		getFlowUUID,
		getStepOfferCode,
		getMainOfferCode,

		isUpsellDiscountModalOpen,
		setIsUpsellDiscountModalOpen,

		upsellDiscount,
		setUpsellDiscount,

		handleRejectFlow,
		rejectV2,

		purchases,
		setPurchases,

		flow,
		step,

		accept,
		reject,

		isDiscountAccepted,
		setIsDiscountAccepted
	}

	return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
};

export const useFlow = () => {
	const context = useContext(FlowContext);
	if (context === undefined) {
		throw new Error('useFlow está fora de FlowProvider.');
	}
	return context;
};
